// Session download helpers — shared between apps that need to export session
// data (reporting, match-tracker-legacy, match-tracker-cloud, etc).
//
// Two flavours of helper live here:
// 1. "Broadcast-API" helpers — CSV/JSON/Parquet fetched straight from the
//    broadcast API. These take the broadcast base URL + session id so they
//    stay Redux-free and work across apps with different server configs.
// 2. "Data-in" helpers — JSON/zip helpers that accept already-fetched data
//    (players, pitch). Callers own the fetch (via RTK Query, plain fetch,
//    etc); this layer only handles serialisation + saveBlob.
//
// GOTCHA: Don't show a toast before `downloadPagePdf()` — `window.print()` is
// synchronous and the toast would appear in the print preview.
import { downloadFromUrl, saveBlob } from '@frontend/utils'
import JSZip from 'jszip'
import { SESSION_DOWNLOAD_DEFINITIONS } from './download-definitions'

const EVENTS_JSON_DEFINITION = SESSION_DOWNLOAD_DEFINITIONS.events_json
const EVENTS_CSV_DEFINITION = SESSION_DOWNLOAD_DEFINITIONS.events_csv
const SPORTSCODE_DEFINITION = SESSION_DOWNLOAD_DEFINITIONS.sportscode_xml
const PLAYER_METRICS_CSV_DEFINITION =
  SESSION_DOWNLOAD_DEFINITIONS.player_metrics_csv
const SESSION_METADATA_CSV_DEFINITION =
  SESSION_DOWNLOAD_DEFINITIONS.session_metadata_csv
const PHYSICAL_METRICS_CSV_DEFINITION =
  SESSION_DOWNLOAD_DEFINITIONS.physical_metrics_csv
const POSITION_PACKETS_PARQUET_DEFINITION =
  SESSION_DOWNLOAD_DEFINITIONS.position_packets_parquet

/**
 * Formats an ISO date string to YYYY-MM-DD for use in filenames.
 */
function formatFilenameDate(dateStr: string): string {
  const date = new Date(dateStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Strips characters that are unsafe in filenames on Windows/macOS/Linux
 * and trims to 200 chars to keep filesystems happy.
 */
function safeName(name: string): string {
  const stripped = (name || 'session')
    .replace(/[\\/:"*?<>|]+/g, '')
    .slice(0, 200)

  return stripped || 'session'
}

/**
 * Extracts a filename from a `Content-Disposition` header value, e.g.
 *   'attachment; filename="session_123_events.csv"'
 *   'attachment; filename=data.csv'
 *   'inline; filename*=UTF-8\'\'Session%20Data.csv'
 * Returns null if the header is absent or carries no filename.
 */
function extractFilenameFromContentDisposition(
  header: string | null
): string | null {
  if (!header) return null

  // Try filename*= (RFC 5987 encoded filename)
  const encodedMatch = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1])
    } catch (e) {
      // Fall through to regular filename extraction
    }
  }

  // Try filename= (quoted or unquoted)
  const match = header.match(/filename=["']?([^;"']+)["']?/i)

  return match ? match[1] : null
}

/**
 * Options controlling a shared download request. Both fields are opt-in so
 * existing callers that pass no options (Reporting) keep today's behavior
 * exactly as-is.
 */
export interface DownloadRequestOptions {
  /**
   * Unit system forwarded as `?units=<unit>` for endpoints where the shared
   * definitions mark `supportsUnit: true`.
   */
  unit?: string
  /**
   * When true, prefer the filename from the response's `Content-Disposition`
   * header over the client-built name, falling back to the client-built name
   * when the header is absent. Match Tracker opts into this; Reporting never
   * does, so its filenames keep being built client-side unconditionally.
   */
  preferHeaderFilename?: boolean
  /**
   * Organisation id forwarded as `?orgId=<orgId>` so downloads are scoped to
   * the caller's selected org, same as the RTK Query session/event endpoints.
   */
  orgId?: string
}

async function downloadSessionBlob(
  endpoint: string,
  filename: string,
  errorLabel: string,
  accept?: string,
  preferHeaderFilename?: boolean
): Promise<void> {
  const response = await fetch(endpoint, {
    credentials: 'include',
    ...(accept ? { headers: { Accept: accept } } : {})
  })
  if (!response.ok) {
    throw new Error(`Failed to download ${errorLabel}: ${response.statusText}`)
  }
  const blob = await response.blob()
  const resolvedFilename = preferHeaderFilename
    ? safeName(
        extractFilenameFromContentDisposition(
          response.headers.get('Content-Disposition')
        ) || filename
      )
    : filename
  saveBlob(blob, resolvedFilename)
}

/**
 * Triggers the browser's native print dialog to save the current page as PDF.
 */
export function downloadPagePdf(): void {
  window.print()
}

/**
 * Builds the `{baseUrl}/game/session:{id}{path}` URL used by every
 * broadcast-API download. `baseUrl` is resolved per-app (usually via
 * `selectServerUrl('broadcastApi')`) — it includes any `/api` suffix the
 * broadcast server expects and respects the currently selected region.
 */
export function broadcastSessionUrl(
  baseUrl: string,
  sessionId: string,
  path: string
): string {
  return `${baseUrl.replace(/\/$/, '')}/game/session:${sessionId}${path}`
}

/**
 * Appends the given query params to an endpoint path, skipping any that are
 * absent/empty. Used where more than the `units` param is needed — the events
 * file selects JSON via `?format=json` on the same route as its CSV default.
 */
function withParams(
  endpoint: string,
  params: Record<string, string | undefined>
): string {
  const query = Object.entries(params)
    .filter((entry): entry is [string, string] => {
      const [, value] = entry
      return value !== undefined && value !== ''
    })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

  return query ? `${endpoint}?${query}` : endpoint
}

/**
 * Downloads the customer events file as CSV for a given session.
 *
 * CSV is the endpoint's default output, so no format param is needed. The
 * route pins its own response Content-Type, so the `Accept` header sent here
 * is advisory and doesn't change the wire response.
 */
export async function downloadEventsCSV(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(EVENTS_CSV_DEFINITION.endpoint ?? '/eventsData', {
        units: options?.unit,
        orgId: options?.orgId
      })
    ),
    `${date} ${name} Events.csv`,
    'events CSV',
    EVENTS_CSV_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads the session's events as a Sportscode XML timeline — the
 * format used by Hudl Sportscode for tagged video analysis.
 *
 * The route pins its response to XML (it ignores the client's Accept header),
 * so the `Accept: application/xml` sent here is advisory and a no-op on the
 * wire.
 */
export async function downloadSportscode(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(SPORTSCODE_DEFINITION.endpoint ?? '/eventsSportsCode', {
        units: options?.unit,
        orgId: options?.orgId
      })
    ),
    `${date} ${name} Sportscode.xml`,
    'sportscode XML',
    SPORTSCODE_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads the customer events file as JSON for a given session.
 *
 * Same route as the CSV default; JSON is selected with `?format=json`. The
 * route pins its response Content-Type per format, so the `Accept` header sent
 * here is advisory and the format param is what actually switches the output.
 */
export async function downloadEventsJSON(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(EVENTS_JSON_DEFINITION.endpoint ?? '/eventsData', {
        format: 'json',
        units: options?.unit,
        orgId: options?.orgId
      })
    ),
    `${date} ${name} Events.json`,
    'events JSON',
    EVENTS_JSON_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads player metrics as a CSV file for a given session.
 *
 * Sends `Accept: text/csv` per the shared definitions — confirmed against
 * the backend (`player_metrics_csv.go`, which hardcodes
 * `Content-Type: text/csv` on the response and never reads the request's
 * Accept header) that this is a no-op on the wire, not a behavior change.
 */
export async function downloadPlayerMetricsCSV(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(PLAYER_METRICS_CSV_DEFINITION.endpoint ?? '/playerMetrics', {
        units: options?.unit,
        orgId: options?.orgId
      })
    ),
    `${date} ${name} Player Metrics.csv`,
    'player metrics CSV',
    PLAYER_METRICS_CSV_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads the session metadata file as CSV for a given session — the shared
 * identity/context file the other download files join against.
 *
 * `supportsUnit: false`: metadata is not unit-rendered, so no `?units=` is
 * sent even when a unit is selected — matching the definition and the UI's
 * "Always original recorded units" grouping.
 */
export async function downloadSessionMetadataCSV(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(
        SESSION_METADATA_CSV_DEFINITION.endpoint ?? '/sessionMetadata',
        {
          orgId: options?.orgId
        }
      )
    ),
    `${date} ${name} Session Metadata.csv`,
    'session metadata CSV',
    SESSION_METADATA_CSV_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads the physical metrics file as CSV for a given session — the
 * spec-shaped per-player physical file (distance/speed/work/power metrics).
 */
export async function downloadPhysicalMetricsCSV(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const date = formatFilenameDate(sessionDate)
  const name = options?.preferHeaderFilename
    ? safeName(sessionName)
    : sessionName
  await downloadSessionBlob(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(
        PHYSICAL_METRICS_CSV_DEFINITION.endpoint ?? '/physicalMetrics',
        { units: options?.unit, orgId: options?.orgId }
      )
    ),
    `${date} ${name} Physical Metrics.csv`,
    'physical metrics CSV',
    PHYSICAL_METRICS_CSV_DEFINITION.acceptHeader,
    options?.preferHeaderFilename
  )
}

/**
 * Downloads position packet data as a Parquet file for a given session. Still
 * live for American/Canadian football; soccer sessions swap this out for the
 * per-team tracking manifest and hide this option.
 *
 * `/packets` returns a GCS signed URL (via `Accept: application/json`) rather
 * than streaming the file, so the signed URL is handed to an anchor
 * (`downloadFromUrl`) — not a fetch, so GCS's missing CORS doesn't block
 * it. `options` only carries `orgId`: this endpoint renders no per-request
 * unit (`supportsUnit: false`) and its filename was never header-driven.
 */
export async function downloadPositionPacketsParquet(
  baseUrl: string,
  sessionId: string,
  sessionDate: string,
  sessionName: string,
  options?: DownloadRequestOptions
): Promise<void> {
  const res = await fetch(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      withParams(POSITION_PACKETS_PARQUET_DEFINITION.endpoint ?? '/packets', {
        orgId: options?.orgId
      })
    ),
    {
      credentials: 'include',
      headers: {
        Accept:
          POSITION_PACKETS_PARQUET_DEFINITION.acceptHeader ?? 'application/json'
      }
    }
  )
  if (!res.ok) {
    throw new Error(`Failed to download position packets: ${res.statusText}`)
  }
  const { url } = (await res.json()) as { url: string }
  downloadFromUrl(
    url,
    `${formatFilenameDate(sessionDate)} ${safeName(sessionName)} Position Packets.parquet`
  )
}

/**
 * Builds the `YYYY-MM-DD sessionName suffix.ext` convention used by all
 * JSON/zip helpers. Date prefix is skipped when `sessionDate` is empty so
 * legacy callers that don't track the session date still get a clean name.
 */
export function buildFilename(
  sessionDate: string,
  sessionName: string,
  suffix: string
): string {
  const name = safeName(sessionName)

  return sessionDate
    ? `${formatFilenameDate(sessionDate)} ${name} ${suffix}`
    : `${name} ${suffix}`
}

/**
 * Saves an already-fetched session-players payload as a pretty-printed JSON
 * file. Caller typically reads `event.data.players` from the raw session
 * event.
 */
export function downloadSessionPlayersJSON(
  players: unknown,
  sessionDate: string,
  sessionName: string
): void {
  const blob = new Blob([JSON.stringify(players, null, 2)], {
    type: 'application/json'
  })
  saveBlob(
    blob,
    buildFilename(sessionDate, sessionName, 'Session Players.json')
  )
}

/**
 * Shape of the pitch object we need for the anchor/points zip.
 * Mirrors `event.data.pitch` on a raw session event: both sub-objects are
 * maps keyed by point/anchor id. We type-narrow to `unknown` to stay loose
 * across sports.
 */
export interface PitchAnchorPayload {
  anchors: unknown
  pitch: unknown
}

/**
 * Zips the pitch anchors + points as two JSON files and saves the archive.
 * `pitch.pitch` is the points map (named this way by the backend, not a
 * typo); `pitch.anchors` is the anchor map.
 */
export async function downloadPitchAnchorZip(
  pitch: PitchAnchorPayload,
  sessionName: string
): Promise<void> {
  if (!pitch.anchors) throw new Error('No pitch anchors found')
  if (!pitch.pitch) throw new Error('No pitch points found')

  const zip = new JSZip()
  zip.file('anchors.json', JSON.stringify(pitch.anchors, null, 2))
  zip.file('points.json', JSON.stringify(pitch.pitch, null, 2))

  const blob = await zip.generateAsync({ type: 'blob' })
  saveBlob(blob, `${safeName(sessionName).replace(/\s+/g, '_')}.zip`)
}
